import apiClient from "../../api/client";

// =============================================
// HELPER FUNCTIONS
// =============================================

const extractData = (response) => {
  if (response?.data?.data?.data && Array.isArray(response.data.data.data)) {
    return response.data.data.data;
  }
  if (response?.data?.data && Array.isArray(response.data.data)) {
    return response.data.data;
  }
  if (response?.data && Array.isArray(response.data)) {
    return response.data;
  }
  if (Array.isArray(response)) {
    return response;
  }
  return [];
};

const extractSingle = (response) => {
  if (response?.data?.data) {
    return response.data.data;
  }
  if (response?.data) {
    return response.data;
  }
  return response;
};

// =============================================
// CUSTOMER CRUD
// =============================================

export const getCustomers = async (filters = {}) => {
  try {
    const params = new URLSearchParams();
    Object.keys(filters).forEach(key => {
      if (filters[key] !== undefined && filters[key] !== null && filters[key] !== '') {
        params.append(key, filters[key]);
      }
    });
    const response = await apiClient.get(`/customers${params.toString() ? `?${params.toString()}` : ''}`);
    console.log("Get customers response:", response);
    return extractData(response);
  } catch (error) {
    console.error("Error fetching customers:", error);
    return [];
  }
};

export const getCustomerById = async (id) => {
  try {
    const response = await apiClient.get(`/customers/${id}`);
    console.log("Get customer by ID response:", response);
    return extractSingle(response);
  } catch (error) {
    console.error("Error fetching customer by ID:", error);
    throw error;
  }
};

export const createCustomer = async (customerData) => {
  return addCustomer(customerData);
};

export const addCustomer = async (customerData) => {
  try {
    console.log("📦 Original customer data received:", customerData);

    const payload = {
      name: customerData.name,
      email: customerData.email,
      mobile: customerData.mobile,
      alternateMobile: customerData.alternateMobile || null,
      address: customerData.address || null,
      landmark: customerData.landmark || null,
      countryId: customerData.countryId ? Number(customerData.countryId) : null,
      stateId: customerData.stateId ? Number(customerData.stateId) : null,
      cityId: customerData.cityId ? Number(customerData.cityId) : null,
      pincodeId: customerData.pincodeId ? Number(customerData.pincodeId) : null,
      gstNumber: customerData.gstNumber || null,
      panNumber: customerData.panNumber || null,
      aadhaarNumber: customerData.aadhaarNumber || null,
      customerType: customerData.customerType || "regular",
      creditLimit: Number(customerData.creditLimit) || 0,
      creditDays: Number(customerData.creditDays) || 0,
      createdBy: customerData.createdBy ? Number(customerData.createdBy) : null,
      status: customerData.status || "active",
      notes: customerData.notes || null,
    };

    console.log("📤 Sending payload to backend:", JSON.stringify(payload, null, 2));

    const response = await apiClient.post("/customers", payload);
    console.log("✅ Add customer response:", response.data);
    return response.data;
  } catch (error) {
    console.error("❌ Error adding customer:", error);
    console.error("Error response data:", error.response?.data);
    throw error;
  }
};

export const updateCustomer = async (id, customerData) => {
  try {
    console.log("📦 Updating customer ID:", id);
    console.log("Update data received:", customerData);

    const payload = {
      name: customerData.name,
      email: customerData.email,
      mobile: customerData.mobile,
      alternateMobile: customerData.alternateMobile || null,
      address: customerData.address || null,
      landmark: customerData.landmark || null,
      countryId: customerData.countryId ? Number(customerData.countryId) : null,
      stateId: customerData.stateId ? Number(customerData.stateId) : null,
      cityId: customerData.cityId ? Number(customerData.cityId) : null,
      pincodeId: customerData.pincodeId ? Number(customerData.pincodeId) : null,
      gstNumber: customerData.gstNumber || null,
      panNumber: customerData.panNumber || null,
      aadhaarNumber: customerData.aadhaarNumber || null,
      customerType: customerData.customerType || "regular",
      creditLimit: Number(customerData.creditLimit) || 0,
      creditDays: Number(customerData.creditDays) || 0,
      status: customerData.status || "active",
      notes: customerData.notes || null,
    };

    console.log("📤 Sending update payload:", JSON.stringify(payload, null, 2));

    const response = await apiClient.put(`/customers/${id}`, payload);
    console.log("✅ Update response:", response.data);
    return response.data;
  } catch (error) {
    console.error("❌ Error updating customer:", error);
    console.error("Error response:", error.response?.data);
    throw error;
  }
};

export const deleteCustomer = async (id) => {
  try {
    const response = await apiClient.delete(`/customers/${id}`);
    console.log("Delete customer response:", response);
    return response.data;
  } catch (error) {
    console.error("Error deleting customer:", error);
    throw error;
  }
};

// =============================================
// CUSTOMER CONNECTIONS - ✅ ADDED updateConnection
// =============================================

export const getCustomerConnections = async (customerId) => {
  try {
    const response = await apiClient.get(`/customers/${customerId}/connections`);
    return extractData(response);
  } catch (error) {
    console.error("Error fetching customer connections:", error);
    return [];
  }
};

export const createCustomerConnection = async (connectionData) => {
  try {
    console.log("📦 Creating connection:", connectionData);
    const response = await apiClient.post("/customers/connections", connectionData);
    return response.data;
  } catch (error) {
    console.error("Error creating connection:", error);
    throw error;
  }
};

// ✅ ADDED: Update Connection
export const updateConnection = async (id, connectionData) => {
  try {
    console.log(`📦 Updating connection ${id}:`, connectionData);
    const response = await apiClient.put(`/customers/connections/${id}`, connectionData);
    return response.data;
  } catch (error) {
    console.error("Error updating connection:", error);
    throw error;
  }
};

// ✅ ADDED: Delete Connection (optional)
export const deleteConnection = async (id) => {
  try {
    const response = await apiClient.delete(`/customers/connections/${id}`);
    return response.data;
  } catch (error) {
    console.error("Error deleting connection:", error);
    throw error;
  }
};

// =============================================
// CUSTOMER BOOKINGS
// =============================================

export const getCustomerBookings = async (customerId, filters = {}) => {
  try {
    const params = new URLSearchParams({ customer_id: customerId, ...filters });
    const response = await apiClient.get(`/customers/bookings?${params.toString()}`);
    return extractData(response);
  } catch (error) {
    console.error("Error fetching customer bookings:", error);
    return [];
  }
};

export const createCustomerBooking = async (bookingData) => {
  try {
    console.log("📦 Creating booking:", bookingData);
    const response = await apiClient.post("/customers/bookings", bookingData);
    return response.data;
  } catch (error) {
    console.error("Error creating booking:", error);
    throw error;
  }
};

export const updateBookingStatus = async (id, statusData) => {
  try {
    const response = await apiClient.put(`/customers/bookings/${id}/status`, statusData);
    return response.data;
  } catch (error) {
    console.error("Error updating booking status:", error);
    throw error;
  }
};

// =============================================
// CUSTOMER ORDERS
// =============================================

export const getCustomerOrders = async (customerId, filters = {}) => {
  try {
    const params = new URLSearchParams({ customer_id: customerId, ...filters });
    const response = await apiClient.get(`/customers/orders?${params.toString()}`);
    return extractData(response);
  } catch (error) {
    console.error("Error fetching customer orders:", error);
    return [];
  }
};

// =============================================
// CUSTOMER INVOICES
// =============================================

export const getCustomerInvoices = async (customerId, filters = {}) => {
  try {
    const params = new URLSearchParams({ customer_id: customerId, ...filters });
    const response = await apiClient.get(`/customers/invoices?${params.toString()}`);
    return extractData(response);
  } catch (error) {
    console.error("Error fetching customer invoices:", error);
    return [];
  }
};

export const downloadInvoice = async (invoiceId, format = 'pdf') => {
  try {
    const response = await apiClient.get(`/invoices/${invoiceId}/download?format=${format}`, {
      responseType: 'blob'
    });
    return response.data;
  } catch (error) {
    console.error("Error downloading invoice:", error);
    throw error;
  }
};

// =============================================
// CUSTOMER DASHBOARD
// =============================================

export const getCustomerDashboardStats = async () => {
  try {
    const response = await apiClient.get("/customers/dashboard/stats");
    return extractSingle(response);
  } catch (error) {
    console.error("Error fetching dashboard stats:", error);
    return {};
  }
};

export const getCustomerRecentActivity = async (limit = 10) => {
  try {
    const response = await apiClient.get(`/customers/dashboard/recent-activity?limit=${limit}`);
    return extractData(response);
  } catch (error) {
    console.error("Error fetching recent activity:", error);
    return [];
  }
};

// =============================================
// EXPORT ALL
// =============================================

// Named exports already defined above
const customerService = {
  getCustomers,
  getCustomerById,
  createCustomer,
  addCustomer,
  updateCustomer,
  deleteCustomer,
  getCustomerConnections,
  createCustomerConnection,
  updateConnection,        // ✅ ADDED
  deleteConnection,        // ✅ ADDED
  getCustomerBookings,
  createCustomerBooking,
  updateBookingStatus,
  getCustomerOrders,
  getCustomerInvoices,
  downloadInvoice,
  getCustomerDashboardStats,
  getCustomerRecentActivity,
};

export default customerService;